import './DemoSkillField.css';

import React from 'react';

import { Timestamp } from 'firebase/firestore';

import PerCoachCommentsSection
  from '../../components/common/PerCoachCommentsSection/PerCoachCommentsSection';
import SkillJudge from '../../components/common/SkillJudge/SkillJudge';
import {
  PerCoachCommentEntry,
  SkillFieldValue,
} from '../../types/athlete';
import { FormField, getSkillConfig } from '../../types/formSchema';
import { generateCommentId } from '../../utils/ids';
import DemoPhotoUpload from './DemoPhotoUpload';
import DemoVideoUpload from './DemoVideoUpload';

export interface DemoSkillFieldProps {
  field: FormField;
  fieldValue: SkillFieldValue;
  onChange: (next: SkillFieldValue) => void;
  /** When true, renders read-only placeholder tiles for photo/video instead of upload widgets. */
  placeholderUploads?: boolean;
  /** Stable id used to key the comment input per subject (athlete or preview). */
  subjectId: string;
  /** UID of the current coach — enables write mode for comments. */
  currentCoachUid?: string;
  /** Display name of the current coach. */
  currentCoachName?: string;
}

const DemoSkillField: React.FC<DemoSkillFieldProps> = ({
  field,
  fieldValue,
  onChange,
  placeholderUploads = false,
  subjectId,
  currentCoachUid,
  currentCoachName,
}) => {
  if (field.type === 'heading') {
    return <h3 className="score-athlete-heading-field">{field.label}</h3>;
  }
  const cfg = getSkillConfig(field);
  const photoValue = fieldValue.photo ?? null;
  const videoValue = fieldValue.video ?? null;
  const ratingValue = fieldValue.rating ?? 0;

  return (
    <div className="score-athlete-skill-row">
      {cfg.rating ? (
        <SkillJudge
          title={field.label}
          value={ratingValue}
          maxRating={cfg.maxRating}
          onChange={(val) => onChange({ ...fieldValue, rating: val })}
        />
      ) : (
        <h3 className="score-athlete-skill-title">{field.label}</h3>
      )}
      {cfg.comment ? (
        <PerCoachCommentsSection
          key={`comment-${subjectId}-${field.id}`}
          comments={fieldValue.comments ?? []}
          currentCoachUid={currentCoachUid}
          currentCoachName={currentCoachName}
          onAddComment={currentCoachUid && currentCoachName ? async (text) => {
            const newEntry: PerCoachCommentEntry = {
              id: generateCommentId(),
              text,
              coachName: currentCoachName,
              coachUid: currentCoachUid,
              createdAt: Timestamp.now(),
            };
            onChange({ ...fieldValue, comments: [...(fieldValue.comments ?? []), newEntry] });
          } : undefined}
          onEditComment={currentCoachUid ? async (commentId, newText) => {
            const updated = (fieldValue.comments ?? []).map((e) =>
              e.id === commentId ? { ...e, text: newText } : e,
            );
            onChange({ ...fieldValue, comments: updated });
          } : undefined}
          onDeleteComment={currentCoachUid ? async (commentId) => {
            const updated = (fieldValue.comments ?? []).filter((e) => e.id !== commentId);
            onChange({ ...fieldValue, comments: updated });
          } : undefined}
        />
      ) : null}
      {cfg.photo ? (
        <div className="score-athlete-photo-field">
          {placeholderUploads ? (
            <div className="demo-skill-field-upload-placeholder" aria-hidden>
              Photo upload
            </div>
          ) : (
            <DemoPhotoUpload
              compactEmpty
              label={`${field.label} photo`}
              imageUrl={photoValue?.imageUrl}
              onImageUpload={(payload) => {
                if (!payload.imageUrl) {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { photo: _removed, ...rest } = fieldValue;
                  onChange(rest);
                  return;
                }
                onChange({
                  ...fieldValue,
                  photo: {
                    imageUrl: payload.imageUrl,
                    imageThumbnailUrl: payload.imageThumbnailUrl,
                    storagePathFull: payload.storagePathFull,
                    storagePathThumb: payload.storagePathThumb,
                  },
                });
              }}
            />
          )}
        </div>
      ) : null}
      {cfg.video ? (
        <div className="score-athlete-photo-field">
          {placeholderUploads ? (
            <div className="demo-skill-field-upload-placeholder" aria-hidden>
              Video upload
            </div>
          ) : (
            <DemoVideoUpload
              compactEmpty
              label={`${field.label} video`}
              videoUid={videoValue?.videoUid}
              onVideoChange={(val) => {
                if (val) {
                  onChange({ ...fieldValue, video: val });
                } else {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { video: _removed, ...rest } = fieldValue;
                  onChange(rest);
                }
              }}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default DemoSkillField;
